import { Board } from "./board";
import { Elim, Step } from "./types";
import { idx, peers } from "./util";

/** Plays the pre-computed solve trace over a Board, tracking candidate (pencil)
 *  marks as it goes. Placement steps drop a letter (and clear it from peers);
 *  elimination steps rule out specific candidates — which the renderer shows
 *  struck through for that step, then gone on the next. State is recomputed from
 *  the givens on each move, so stepping backwards is exact. (Stuck mode only.) */
export class Walkthrough {
  /** Per-cell candidate sets at the current step. */
  candidates: Set<string>[] = [];
  /** Candidates ruled out by the current step (to draw struck through). */
  struck: Elim[] = [];
  private i = 0;

  constructor(private board: Board, readonly steps: Step[], private alphabet: string[]) {
    this.goto(0);
  }

  get index(): number {
    return this.i;
  }
  get total(): number {
    return this.steps.length;
  }
  get current(): Step | null {
    return this.i > 0 ? this.steps[this.i - 1] : null;
  }
  get isDone(): boolean {
    return this.i >= this.steps.length;
  }

  next(): boolean {
    if (this.i >= this.steps.length) return false;
    this.goto(this.i + 1);
    return true;
  }
  prev(): boolean {
    if (this.i === 0) return false;
    this.goto(this.i - 1);
    return true;
  }
  reset(): void {
    this.goto(0);
  }

  /** Replay steps 0..n from the givens, rebuilding the board and candidates. */
  goto(n: number): void {
    this.i = n;
    this.board.clearAll();
    const cand: Set<string>[] = [];
    for (let k = 0; k < 81; k++) cand[k] = this.board.given[k] ? new Set() : new Set(this.alphabet);
    // Propagate the givens (each rules its letter out of its peers).
    for (let k = 0; k < 81; k++) {
      if (this.board.given[k]) {
        const L = this.board.solution[k];
        for (const p of peers(k)) cand[p].delete(L);
      }
    }
    for (let s = 0; s < n; s++) {
      const step = this.steps[s];
      if (step.fills && step.fills.length) {
        // A placement: one cell, or a whole word at once.
        for (const f of step.fills) {
          const c = idx(f.cell[0], f.cell[1]);
          this.board.set(c, f.letter);
          cand[c] = new Set();
          for (const p of peers(c)) cand[p].delete(f.letter);
        }
      } else {
        for (const e of step.eliminations) cand[idx(e.cell[0], e.cell[1])].delete(e.letter);
      }
    }
    this.candidates = cand;
    const cur = this.current;
    this.struck = cur && !cur.cell ? cur.eliminations : [];
  }
}
